import { Route, Routes, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { selectUserRoles, selectUserPermissions } from '@store/auth';

import ApplicationTemplate from '@dashboardComponents/ApplicationTemplate/ApplicationTemplate';

import StorefrontIcon from '@mui/icons-material/Storefront';
import PaymentIcon from '@mui/icons-material/Payment';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import PublicIcon from '@mui/icons-material/Public';
import KeyIcon from '@mui/icons-material/Key';
import ForumIcon from '@mui/icons-material/Forum';
import SettingsIcon from '@mui/icons-material/Settings';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import BuildIcon from '@mui/icons-material/Build';
import TableRowsIcon from '@mui/icons-material/TableRows';
import WebIcon from '@mui/icons-material/Web';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import Currencies from './pages/currencies/Currencies';
import CurrencyForm from './pages/currencies/CurrencyForm';
import Gateways from './pages/gateways/Gateways';
import GatewayForm from './pages/gateways/GatewayForm';
import GatewayMapping from './pages/gateway-mapping/GatewayMapping';
import GatewayProperties from './pages/gateway-properties/GatewayProperties';
import MerchantForm from './pages/merchants/MerchantForm';
import MethodForm from './pages/methods/MethodForm';
import PaymentMerchants from './pages/merchants/PaymentMerchants';
import PaymentMerchantsTiled from './pages/merchants/PaymentMerchantsTiled';
import PaymentMethods from './pages/methods/PaymentMethods';
import Platforms from './pages/platforms/Platforms';
import PlatformForm from './pages/platforms/PlatformForm';
import PropertyKeys from './pages/properties/PropertyKeys';
import PropertyKeyForm from './pages/properties/PropertyKeyForm';
import ContextualMessagesList from './pages/contextual-messages/ContextualMessagesList';
import ContextualMessagesForm from './pages/contextual-messages/ContextualMessagesForm';
import MaintenanceRulesList from './pages/maintenance-rules/MaintenanceRulesList';
import MaintenanceRulesForm from './pages/maintenance-rules/MaintenanceRulesForm';
import Tabs from './pages/tabs/Tabs';
import TabForm from './pages/tabs/TabForm';
import MethodMatrix from './pages/method-matrix/MethodMatrix';

import {
  CURRENCY_LIST_ROUTE,
  GATEWAY_LIST_ROUTE,
  GATEWAY_MAPPING_ROUTE,
  GATEWAY_PROPERTIES,
  TAB_PRIORITY_ROUTE,
  LOB_LIST_ROUTE,
  METHOD_LIST_ROUTE,
  PLATFORM_LIST_ROUTE,
  PROPERTY_KEY_LIST_ROUTE,
  CONTEXTUAL_MESSAGES_LIST_ROUTE,
  MAINTENANCE_RULES_LIST_ROUTE,
  TAB_LIST_ROUTE,
  METHOD_MATRIX_LIST_ROUTE,
} from './payment-options.breadcrumbs';

import { ROLES } from '../../app/App.constants';

import { getFirstRoute } from '../../utils/utils';
import TabPriority from './pages/tab-priority/TabPriority';

const tabs = [
  {
    title: 'Lines of Business',
    icon: <StorefrontIcon />,
    path: LOB_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Bulk Gateway Mapping',
    icon: <ForkRightIcon />,
    path: GATEWAY_MAPPING_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Update Gateway Properties',
    icon: <BuildIcon />,
    path: GATEWAY_PROPERTIES,
    roles: [ROLES.ROLE_PAYOPT_EDIT],
  },
  { divider: true },
  {
    title: 'Payment Methods',
    icon: <PaymentIcon />,
    path: METHOD_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Currencies',
    icon: <PriceChangeIcon />,
    path: CURRENCY_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Gateways',
    icon: <PublicIcon />,
    disabled: false,
    path: GATEWAY_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  { divider: true },
  {
    title: 'Tabs',
    icon: <TableRowsIcon />,
    disabled: false,
    path: TAB_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Tab Priority',
    icon: <LowPriorityIcon />,
    path: TAB_PRIORITY_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_EDIT],
  },
  { divider: true },
  {
    title: 'Platforms',
    icon: <WebIcon />,
    disabled: false,
    path: PLATFORM_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  {
    title: 'Property Keys',
    icon: <KeyIcon />,
    disabled: false,
    path: PROPERTY_KEY_LIST_ROUTE,
    roles: [ROLES.ROLE_PAYOPT_VIEW, ROLES.ROLE_PAYOPT_EDIT],
  },
  { divider: true },
  {
    title: 'Contextual Messages',
    icon: <ForumIcon />,
    disabled: false,
    path: CONTEXTUAL_MESSAGES_LIST_ROUTE,
    roles: [
      ROLES.ROLE_CONTEXTUAL_MESSAGES_VIEW,
      ROLES.ROLE_CONTEXTUAL_MESSAGES_EDIT,
    ],
  },
  {
    title: 'Maintenance Rules',
    icon: <SettingsIcon />,
    disabled: false,
    path: MAINTENANCE_RULES_LIST_ROUTE,
    roles: [ROLES.ROLE_MAINTENANCE_RULE_VIEW, ROLES.ROLE_MAINTENANCE_RULE_EDIT],
  },
  { divider: true },
  {
    title: 'Method Matrix (Bird-eye view)',
    icon: <AutoAwesomeIcon />,
    disabled: false,
    path: METHOD_MATRIX_LIST_ROUTE,
    roles: [ROLES.ROLE_METHOD_MATRIX_VIEW],
  },
];

function Options() {
  const permissions = useSelector(selectUserPermissions);
  const userRoles = useSelector(selectUserRoles);
  const firstRoute = getFirstRoute(userRoles, tabs);

  return (
    <ApplicationTemplate menu={tabs}>
      <Routes>
        <Route
          path="/"
          element={
            <Navigate
              to={firstRoute}
              replace
            />
          }
        />
        <Route
          path="/lobs"
          exact
          element={<PaymentMerchantsTiled />}
        />
        <Route
          path="/lobs/old"
          exact
          element={<PaymentMerchants />}
        />
        <Route
          path="lobs/new"
          exact
          element={<MerchantForm />}
        />
        <Route
          path="lobs/:id"
          exact
          element={<MerchantForm />}
        />
        <Route
          path="/methods"
          exact
          element={<PaymentMethods />}
        />
        <Route
          path="methods/new"
          exact
          element={<MethodForm />}
        />
        <Route
          path="methods/:id"
          exact
          element={<MethodForm />}
        />
        <Route
          path="/currencies"
          exact
          element={<Currencies />}
        />
        <Route
          path="currencies/new"
          exact
          element={<CurrencyForm />}
        />
        <Route
          path="currencies/:id"
          exact
          element={<CurrencyForm />}
        />
        <Route
          path="/gateways"
          exact
          element={<Gateways />}
        />
        <Route
          path="gateways/new"
          exact
          element={<GatewayForm />}
        />
        <Route
          path="gateways/:id"
          exact
          element={<GatewayForm />}
        />
        <Route
          path="/gateway-mapping"
          exact
          element={<GatewayMapping />}
        />
        <Route
          path="/gateway-properties"
          exact
          element={<GatewayProperties />}
        />
        <Route
          path="/tab-priority"
          exact
          element={<TabPriority />}
        />
        <Route
          path="/tabs"
          exact
          element={<Tabs />}
        />
        <Route
          path="tabs/new"
          exact
          element={<TabForm />}
        />
        <Route
          path="tabs/:id"
          exact
          element={<TabForm />}
        />
        <Route
          path="/platforms"
          exact
          element={<Platforms />}
        />
        <Route
          path="platforms/new"
          exact
          element={<PlatformForm />}
        />
        <Route
          path="platforms/:id"
          exact
          element={<PlatformForm />}
        />
        <Route
          path="/property-keys"
          exact
          element={<PropertyKeys />}
        />
        <Route
          path="property-keys/new"
          exact
          element={<PropertyKeyForm />}
        />
        <Route
          path="property-keys/:id"
          exact
          element={<PropertyKeyForm />}
        />
        <Route
          path="/contextual-messages"
          exact
          element={<ContextualMessagesList />}
        />
        <Route
          path="/contextual-messages/new"
          exact
          element={<ContextualMessagesForm />}
        />
        <Route
          path="/maintenance-rules"
          exact
          element={<MaintenanceRulesList permissions={permissions} />}
        />
        <Route
          path="/maintenance-rules/new"
          exact
          element={<MaintenanceRulesForm permissions={permissions} />}
        />
        <Route
          path="/method-matrix"
          exact
          element={<MethodMatrix />}
        />
      </Routes>
    </ApplicationTemplate>
  );
}

export default Options;
